import { matchRoutes } from 'react-router-dom';
import type { RouteAccessProps, RouteConfig } from 'types';
import type { NavigationAction } from 'services/agent/model';
import { KNOWLEDGE_FORM_TYPES, knowledgeFormNavigationSearch } from './knowledge-form';

const RETURN_GROUP_LABELS: Record<string, string> = {
  workbench: 'nav.conversation',
  knowledge: 'AI运营-菜单名称',
  management: 'nav.配置中心',
  dashboard: 'nav.dashboard',
  aide: 'aide.跟单助手',
};

const WORKBENCH_RETURN_LABELS: Record<string, string> = {
  conversation: 'scene.workspace.title.chat',
  review: 'scene.workspace.title.review',
  refund: 'scene.workspace.title.order',
  todo: 'scene.workspace.title.tasks',
  'mercado-faq': 'scene.workspace.title.faq',
};

/** Match the top navigation group and the page title, as in the workspace tabs. */
export function agentReturnPageLabelKeys(
  path: string,
  routeTitleKeys: readonly (string | [string, string])[],
): (string | [string, string])[] {
  const [, groupPath, leafPath] = path.split('/');
  const group = RETURN_GROUP_LABELS[groupPath];
  const leaf = groupPath === 'workbench'
    ? WORKBENCH_RETURN_LABELS[leafPath] ?? routeTitleKeys.at(-1)
    : routeTitleKeys.at(-1);
  return [group, leaf === group ? undefined : leaf]
    .filter((key): key is string | [string, string] => Boolean(key));
}

/** Public IDs match the fett skill catalog; URLs are owned by this app, never by the model. */
export const AGENT_PAGES: Record<string, string> = {
  agent: '/agent',
  // Current agent catalog IDs for business pages already present in Fett.
  'reception-ai-settings': '/aide/qa/nightWatch',
  'knowledge-product-detail': '/knowledge/product?agentView=overview',
  'knowledge-product-detail-sync': '/knowledge/product?agentView=detail-extraction',
  'knowledge-product-upload': '/knowledge/product?agentView=document-import',
  'knowledge-product-conversation-mining': '/knowledge/product?agentView=history',
  'knowledge-product-reception-deposition-unanswered':
    '/knowledge/product?agentView=supplement-knowledge&agentTab=CUSTOMER',
  'knowledge-product-reception-deposition-wrong-answers':
    '/knowledge/product?agentView=supplement-knowledge&agentTab=AI_ERROR_REPLY',
  'data-fulfillment-orders-overview': '/dashboard/deal?agentTab=orderOverview',
  'data-fulfillment-sales-conversion': '/dashboard/deal?agentTab=salesConvert',
  'data-fulfillment-order-loss': '/dashboard/deal?agentTab=orderWastage',
  'data-store-performance-review-analysis-review-data': '/dashboard/deal?agentTab=review',
  'data-store-performance-review-analysis-voc': '/dashboard/reviewAnalysis?agentTab=用户之声',
  'data-store-performance-review-analysis-malicious-buyers':
    '/dashboard/reviewAnalysis?agentTab=恶意买家监测',
  'workbench-mercado-qa': '/workbench/mercado-faq',
  'reception-ai-scenarios': '/aide/qa/officialScenario',
  'reception-automatic-messages-welcome': '/aide/basis/welcome',
  'reception-automatic-messages-timeout-reply': '/aide/basis/autoReply',
  'reception-automatic-messages-follow-request': '/aide/basis/inviteFollower',
  'reception-tools-important-reminders': '/aide/basis/remind',
  'reception-tools-sensitive-words': '/management/shops/sensitive-word',
  'reception-satisfaction-csat-invites': '/aide/csat',
  'knowledge-store': '/knowledge/general',
  ...Object.fromEntries(
    KNOWLEDGE_FORM_TYPES.map((definition_code) => [
      `knowledge-store-${definition_code.toLowerCase().replace(/_/g, '-')}`,
      `/knowledge/general${knowledgeFormNavigationSearch({ definition_code })}`,
    ]),
  ),
  'knowledge-scripts-quick-replies': '/aide/basis/phrase',
  'data-customer-service-performance': '/dashboard/perf',
  'data-store-performance-overview': '/dashboard/platformData',
  'data-store-performance-review-analysis-negative-reviews':
    '/dashboard/reviewAnalysis?agentTab=差评分析',
  'data-ai-qa': '/dashboard/qapair',
  'management-buyers': '/management/buyer/blockList',
  'management-merchants-subscription': '/management/service/market',

  'workbench-conversation': '/workbench/conversation',
  'workbench-review': '/workbench/review',
  'workbench-refund-return': '/workbench/refund/return',
  'workbench-refund-cancel': '/workbench/refund/cancel',
  'workbench-todo': '/workbench/todo',
  'workbench-mercado-faq': '/workbench/mercado-faq',
  knowledge: '/knowledge',
  'knowledge-product': '/knowledge/product',
  'knowledge-marketing': '/knowledge/marketing',
  'knowledge-general': '/knowledge/general',
  'knowledge-boost': '/knowledge/boost',
  management: '/management',
  'management-stores': '/management/shops/store',
  'management-shops-group': '/management/shops/group',
  'management-shops-sensitive-word': '/management/shops/sensitive-word',
  'management-shops-erp': '/management/shops/erp',
  'management-employees-accounts': '/management/staff/account',
  'management-employees-departments': '/management/staff/department',
  'management-buyer-blocklist': '/management/buyer/blockList',
  'management-buyer-easy-refund': '/management/buyer/easyRefundBuyer',
  'management-buyer-wa': '/management/buyer/waBuyerList',
  'management-item-group': '/management/item/group',
  'management-service-market': '/management/service/market',
  'management-service-wa-wallet': '/management/service/waWallet',
  'management-service-enterprise': '/management/service/enterprise',
  'management-service-app-center': '/management/service/appCenter',
  'management-feedback': '/management/feedback',
  dashboard: '/dashboard',
  'dashboard-deal': '/dashboard/deal',
  'dashboard-analysis': '/dashboard/analysis',
  'knowledge-history-mark': '/knowledge/historyMark',
  'knowledge-activity-dashboard': '/knowledge/activityDashboard',
  course: '/course',
  'voc-plus-ai': '/vocplusai',
  'dashboard-perf': '/dashboard/perf',
  'dashboard-todo': '/dashboard/todoOverview',
  'dashboard-reputation': '/dashboard/mercado',
  'dashboard-ai-reply': '/dashboard/aiReply',
  'dashboard-qapair': '/dashboard/qapair',
  'dashboard-platform-data': '/dashboard/platformData',
  'dashboard-review-analysis': '/dashboard/reviewAnalysis',
  'dashboard-activity': '/dashboard/activity',
  aide: '/aide',
  'aide-basis-phrase': '/aide/basis/phrase',
  'aide-basis-auto-reply': '/aide/basis/autoReply',
  'aide-basis-welcome': '/aide/basis/welcome',
  'aide-basis-invite-follower': '/aide/basis/inviteFollower',
  'aide-basis-score': '/aide/basis/score',
  'aide-basis-pause': '/aide/basis/pause',
  'aide-basis-remind': '/aide/basis/remind',
  'aide-basis-todo': '/aide/basis/todo',
  'aide-basis-wa-template': '/aide/basis/waMsgTemp',
  'aide-qa-official': '/aide/qa/officialScenario',
  'aide-qa-shop': '/aide/qa/shopScenario',
  'aide-qa-night-watch': '/aide/qa/nightWatch',
  'aide-qa-store-group': '/aide/qa/storeGroup',
  'aide-order': '/aide/order',
  'aide-pay': '/aide/pay',
  'aide-outstock': '/aide/outstock',
  'aide-logistics': '/aide/logistics',
  'aide-save-order': '/aide/saveOrder',
  'aide-csat': '/aide/csat',
  'aide-review': '/aide/review',
};

export function parseNavigation(raw: unknown): NavigationAction | undefined {
  try {
    const value = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (value?.version !== 1 || !Array.isArray(value.widgets) || value.widgets.length !== 1) return;
    const widget = value.widgets[0];
    if (!widget || typeof widget !== 'object' || Array.isArray(widget)) return;
    if (
      Object.keys(widget).some((key) => !['type', 'id', 'workspace_id', 'selection'].includes(key))
    )
      return;
    if (
      widget.type !== 'workspace_navigation' ||
      typeof widget.id !== 'string' ||
      !widget.id.trim() ||
      widget.id.length > 64
    )
      return;
    if (
      typeof widget.workspace_id !== 'string' ||
      !Object.prototype.hasOwnProperty.call(AGENT_PAGES, widget.workspace_id)
    )
      return;
    if (
      widget.selection !== undefined &&
      (widget.workspace_id !== 'agent' ||
        typeof widget.selection !== 'string' ||
        !/^[A-Za-z0-9_-]{1,128}$/.test(widget.selection))
    )
      return;
    return { id: widget.id, workspace_id: widget.workspace_id, selection: widget.selection };
  } catch {
    return;
  }
}
export function navigationPath(action: NavigationAction) {
  const path = AGENT_PAGES[action.workspace_id];
  return action.selection ? `${path}/${encodeURIComponent(action.selection)}` : path;
}
export function navigationArrived(pathname: string, search: string, destination: string): boolean {
  const [targetPath, targetSearch = ''] = destination.split('?');
  return pathname === targetPath && search.replace(/^\?/, '') === targetSearch;
}
export function workspaceForAgentLocation(pathname: string, search = ''): string | undefined {
  const currentParams = new URLSearchParams(search);
  return Object.entries(AGENT_PAGES)
    .sort((a, b) => b[1].length - a[1].length)
    .find(([, destination]) => {
      const [path, query] = destination.split('?');
      if (query) {
        if (path !== pathname) return false;
        return [...new URLSearchParams(query)].every(([key, value]) => currentParams.get(key) === value);
      }
      return path === pathname || pathname.startsWith(`${path}/`);
    })?.[0];
}
export function canNavigate(path: string, routes: RouteConfig[], props: RouteAccessProps): boolean {
  const matches = matchRoutes(routes, path);
  return Boolean(
    matches?.length &&
      matches.every(
        ({ route }) => !route.path.includes('*') && (!route.access || Boolean(route.access(props))),
      ),
  );
}
